import { useCallback, useContext } from 'react';
import { format } from 'date-fns';
import { useQueryClient } from '@zg-rentals/react-query5';
import { TourActionType } from '@zg-rentals/ts-z-calendar-client';
import { SatelliteNextStepType } from '@zg-rentals/ts-satellite-client';
import { logError } from '@zg-rentals/log-error';
import usePageIdentifier from './usePageIdentifier';
import { useOptimisticLeadUpdate } from './useLeadManagementTableQuery';
import { patchConversationMessagesStatusLabel } from './useConversationMessagesQuery';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import { PAGE_NAME } from '../constants/pageName';
import { STATUS_LABEL_TOUR, STATUS_LABEL_TOUR_CANCELED, STATUS_LABEL_TOUR_DECLINED } from '../components/StatusLabel';

export const STATUS_LABELS = {
  [TourActionType.IRENE_CANCEL]: {
    linkId: STATUS_LABEL_TOUR_CANCELED,
    text: 'TOUR CANCELED',
  },
  [TourActionType.IRENE_DECLINE]: {
    linkId: STATUS_LABEL_TOUR_DECLINED,
    text: 'TOUR DECLINED',
  },
  [TourActionType.IRENE_CONFIRM]: {
    linkId: STATUS_LABEL_TOUR,
    text: '', // Custom date
  },
};

type UpdateStatusLabelCache = {
  conversationId: string;
  listingAlias: string;
};

type UpdateStatusLabelCacheAction =
  | TourActionType.IRENE_CANCEL
  | TourActionType.IRENE_DECLINE
  | TourActionType.IRENE_CONFIRM;

export default ({ conversationId, listingAlias }: UpdateStatusLabelCache) => {
  const queryClient = useQueryClient();
  // Only the inbox branch patches the getMessages cache; the context is provided there.
  const { clientTimeZone } = useContext(InboxPageContext);
  const currentPage = usePageIdentifier();
  const optimisticLeadUpdate = useOptimisticLeadUpdate();

  const updateStatusLabelCache = useCallback(
    async (action: UpdateStatusLabelCacheAction, startMs?: number) => {
      try {
        const tourStatusLabel = startMs ? `${format(new Date(startMs), 'MMM d').toUpperCase()} TOUR` : '';

        const newStatusLabel = {
          text: action === TourActionType.IRENE_CONFIRM ? tourStatusLabel : STATUS_LABELS[action].text,
          linkId: STATUS_LABELS[action].linkId,
        };

        if (currentPage === PAGE_NAME.inbox) {
          // The inbox NotesBar reads `statusLabel` from the getMessages React Query cache.
          await patchConversationMessagesStatusLabel(
            queryClient,
            { conversationId, clientTimeZone },
            { text: newStatusLabel.text, linkedId: newStatusLabel.linkId },
          );
        } else if (currentPage === PAGE_NAME.leadManagement) {
          optimisticLeadUpdate(conversationId, (lead) => ({
            ...lead,
            statusLabel: {
              ...lead.statusLabel,
              ...newStatusLabel,
            },
            nextStep: {
              ...lead.nextStep,
              nextStepType: SatelliteNextStepType.LINK,
              link: {
                linkData: '',
                linkId: 'nextStepSendMessage',
                text: 'Message',
                url: `/rental-manager/inbox/${listingAlias}/${conversationId}`,
              },
            },
          }));
        }
      } catch (error) {
        logError({
          error,
          context: {
            details: 'Error modifying status labels in the cache',
            currentPage,
            action,
            listingAlias,
            conversationId,
            startMs,
          },
        });
      }
    },
    [queryClient, clientTimeZone, currentPage, conversationId, listingAlias, optimisticLeadUpdate],
  );

  return updateStatusLabelCache;
};
